/**
 * All valid ISO 639-1 two-letter language codes (~184 languages).
 * Used to validate that the base language code is a real language code
 * before allowing any BCP-47 subtags (script, region).
 */
const ISO_639_1_CODES = new Set([
  "aa", "ab", "ae", "af", "ak", "am", "an", "ar", "as", "av", "ay", "az",
  "ba", "be", "bg", "bh", "bi", "bm", "bn", "bo", "br", "bs",
  "ca", "ce", "ch", "co", "cr", "cs", "cu", "cv", "cy",
  "da", "de", "dv", "dz",
  "ee", "el", "en", "eo", "es", "et", "eu",
  "fa", "ff", "fi", "fj", "fo", "fr", "fy",
  "ga", "gd", "gl", "gn", "gu", "gv",
  "ha", "he", "hi", "ho", "hr", "ht", "hu", "hy", "hz",
  "ia", "id", "ie", "ig", "ii", "ik", "io", "is", "it", "iu",
  "ja", "jv",
  "ka", "kg", "ki", "kj", "kk", "kl", "km", "kn", "ko", "kr", "ks", "ku", "kv", "kw", "ky",
  "la", "lb", "lg", "li", "ln", "lo", "lt", "lu", "lv",
  "mg", "mh", "mi", "mk", "ml", "mn", "mr", "ms", "mt", "my",
  "na", "nb", "nd", "ne", "ng", "nl", "nn", "no", "nr", "nv", "ny",
  "oc", "oj", "om", "or", "os",
  "pa", "pi", "pl", "ps", "pt",
  "qu",
  "rm", "rn", "ro", "ru", "rw",
  "sa", "sc", "sd", "se", "sg", "si", "sk", "sl", "sm", "sn", "so", "sq", "sr", "ss", "st", "su", "sv", "sw",
  "ta", "te", "tg", "th", "ti", "tk", "tl", "tn", "to", "tr", "ts", "tt", "tw", "ty",
  "ug", "uk", "ur", "uz",
  "ve", "vi", "vo",
  "wa", "wo",
  "xh",
  "yi", "yo",
  "za", "zh", "zu"
]);

/**
 * BCP-47 language code validation regex:
 * - Base: 2-3 letter language code (ISO 639-1/639-2)
 * - Optional script subtag: 4 letters (e.g. zh-Hans)
 * - Optional region subtag: 2 letters or 3 digits (e.g. en-US, pt-419)
 * - No variants or additional subtags allowed
 * - Disallows fake scripts (>4 letters) or extra segments (e.g. zh-hansss, en-verylong, en-us-posix)
 * 
 * Accepts:
 *   en, en-us, zh-hans, zh-hant-tw, fi-fi, pt-pt, pt-419
 * Rejects:
 *   zh-hansss, zh-hansss-znssss, en-verylong, en-us-posix
 */
const BCP47_LANG_SCRIPT_REGION = /^[a-z]{2,3}(?:-[a-z]{4})?(?:-(?:[a-z]{2}|\d{3}))?$/;

/**
 * Determines the user's locale from various client-side hints.
 *
 * Detection priority (highest to lowest):
 * 1. URL parameters (?locale=, ?lang=, ?hl=)
 * 2. Path segment (/fi/, /en-US/, etc.)
 * 3. HTML <html lang="..."> attribute
 * 4. Subdomain (fi.example.com)
 *
 * Returns: The first detected locale as a normalized BCP-47-ish tag, or null if none found.
 * Note: Detection order in code reflects this actual sequence, differing from some other comments.
 */
export const detectLocale = (): string | null => {
  const parsedUrl = new URL(window.location.href);

  const queryLocale = localeFromQueryParams(parsedUrl);
  if (queryLocale) {
    return queryLocale;
  }

  const pathLocale = localeFromPath(parsedUrl);
  if (pathLocale) {
    return pathLocale;
  }

  const htmlLocale = localeFromHtmlLang();
  if (htmlLocale) {
    return htmlLocale;
  }

  const subdomainLocale = localeFromSubdomain(parsedUrl);
  if (subdomainLocale) {
    return subdomainLocale;
  }

  // No matching locale found from any source
  return null;
};

/**
 * Returns the visitor's IANA timezone (e.g. "Europe/Helsinki") from the
 * browser, or null when unavailable — old WebViews may lack Intl support
 * or throw, and the field must then be omitted from payloads entirely.
 */
export const getTimeZone = (): string | null => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

/** Checks query parameters (?locale=fi|lang=fi|hl=fi) for a locale */
const localeFromQueryParams = (parsedUrl: URL): string | null => {
  const queryKeys = ["locale", "lang", "hl"];
  for (const queryKey of queryKeys) {
    const candidate = normalizeIfLocale(parsedUrl.searchParams.get(queryKey));
    if (candidate) {
      return candidate;
    }
  }
  return null;
};

/** Checks the first segment of the path for a locale-like tag (/fi, /en-US, etc.) */
const localeFromPath = (parsedUrl: URL): string | null => {
  const firstSegment = parsedUrl.pathname.split("/").filter(Boolean)[0];
  return normalizeIfLocale(firstSegment);
};

/** Checks the subdomain (fi.example.com) for a locale-like tag */
const localeFromSubdomain = (parsedUrl: URL): string | null => {
  const hostParts = parsedUrl.hostname.split(".");
  if (hostParts.length >= 3) {
    const subdomain = hostParts[0];
    return normalizeIfLocale(subdomain);
  }
  return null;
};

/**
 * Extracts and normalizes the locale from the document's <html lang="..."> attribute.
 * Returns a normalized BCP-47-ish tag or null if not found or invalid.
 */
const localeFromHtmlLang = (): string | null => {
  const htmlElement = document.documentElement;
  if (!htmlElement) {
    return null;
  }

  const langAttribute = htmlElement.getAttribute("lang");
  return normalizeIfLocale(langAttribute);
};

/**
 * Validates and normalizes locale input against the whitelist.
 *
 * - Accepts any BCP-47 format (en, en-US, en_Gb, FI-FI, etc.)
 * - Case-insensitive and accepts underscores
 * - Validates base language is in supported whitelist
 * - Validates full format matches BCP-47 (no arbitrary long words)
 * - Returns normalized locale with hyphens (not underscores)
 *
 * Examples:
 *   normalizeIfLocale("en") → "en" (valid)
 *   normalizeIfLocale("en-US") → "en-US" (valid)
 *   normalizeIfLocale("en_gb") → "en-gb" (valid)
 *   normalizeIfLocale("FI-FI") → "fi-fi" (valid)
 *   normalizeIfLocale("fi") → "fi" (valid)
 *   normalizeIfLocale("lol") → null (invalid - not in ISO 639-1)
 *   normalizeIfLocale("en-verylong") → null (invalid format)
 */
const normalizeIfLocale = (rawInput: string | null | undefined): string | null => {
  if (!rawInput) {
    return null;
  }

  // Normalize: convert underscores to hyphens, trim whitespace, lowercase
  const sanitized = rawInput.replace(/_/g, "-").trim().toLowerCase();

  // Extract base language (first segment)
  const baseLang = sanitized.split("-")[0];

  // Check if base language is a valid ISO 639-1 code
  if (!ISO_639_1_CODES.has(baseLang)) {
    return null;
  }

  // Validate the full format matches BCP-47 (no arbitrary long words)
  if (!BCP47_LANG_SCRIPT_REGION.test(sanitized)) {
    return null;
  }

  // Return normalized locale (hyphens, lowercase)
  return sanitized;
};
